
import Vue from "vue"
import Component from "vue-class-component"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {PCReport, PDF_SOURCE_PAGE} from "@/report/PCReport"
import {components} from "@/report/pages/Components"
import CoverPage from "@/report/pages/CoverPage.vue"
import TrendsPage from "@/report/pages/TrendsPage.vue"
import RankingPage from "@/report/pages/RankingPage.vue"
import AsfaBalancePage from "@/report/pages/AsfaBalancePage.vue"
import InsurancePage from "@/report/pages/InsurancePage.vue"
import InvestmentPage from "@/report/pages/InvestmentPage.vue"
import OverviewPage from "@/report/pages/OverviewPage.vue"
import MembershipPage from "@/report/pages/MembershipPage.vue"
import StateMapPage from "@/report/pages/StateMapPage.vue"
import FundMixPage from "@/report/pages/FundMixPage.vue"
import DisclaimerPage from "@/report/pages/DisclaimerPage.vue"
import BackPage from "@/report/pages/BackPage.vue"
import ContactsPage from "@/report/pages/ContactsPage.vue"
import FundOverviewPage from "@/report/pages/FundOverviewPage.vue"
import TransactionBalancePage from "@/report/pages/TransactionBalancePage.vue"
import _ from "lodash"
import CustomBiSectionPage from "@/report/pages/CustomBiSectionPage.vue"
import {Inject} from "vue-property-decorator"
import {PlanService} from "@/superannuation/PlanService"
import {DateRangeService} from "@/DateRangeService"
import DashboardReport from "@/report/DashboardReport.vue"
import DashboardPage from "@/report/pages/DashboardPage.vue"
import FitnessActivityPage from "@/report/pages/FitnessActivityPage.vue"
import SuperScorePage from "@/report/pages/SuperScorePage.vue"
import SsBeneficiaryPage from "@/report/pages/SsBeneficiaryPage.vue"
import SsBalancePage from "@/report/pages/SsBalancePage.vue"
import SsInsurancePage from "@/report/pages/SsInsurancePage.vue"
import SsInvestmentPage from "@/report/pages/SsInvestmentPage.vue"
import SsRetirementPage from "@/report/pages/SsRetirementPage.vue"

class ReportReadyEvent {
  filename: string
  metadata: any
  disposition = "inline"
  errors: Array<string>

  constructor(filename: string) {
    this.filename = filename
  }
}

interface PageBinding {
  report: PCReport
  number: number
  name: string
  engagedOnly: boolean
}

@Component({
  components: {
    FitnessActivityPage,
    DashboardPage,
    DashboardReport,
    CoverPage,
    DisclaimerPage,
    ContactsPage,
    FundOverviewPage,
    TransactionBalancePage,
    OverviewPage,
    TrendsPage,
    RankingPage,
    AsfaBalancePage,
    InsurancePage,
    MembershipPage,
    StateMapPage,
    InvestmentPage,
    FundMixPage,
    BackPage,
    CustomBiSectionPage,
    SuperScorePage,
    SsBeneficiaryPage,
    SsBalancePage,
    SsInsurancePage,
    SsInvestmentPage,
    SsRetirementPage,
  },
})
export default class PolicyCommitteeReport extends Vue {
  @Inject()
  planService: PlanService

  @Inject()
  dateRangeService: DateRangeService

  report: null | PCReport = null
  preview = false
  engagedOnly = false

  pageReady: boolean | null = null

  async created() {
    const planId = <string | null>this.$route.query.planId
    const companyId = <string | null>this.$route.query.companyId
    this.preview = !_.isEmpty(this.$route.query.preview)
    this.engagedOnly = <string | null>this.$route.query.engagedOnly === "true"

    this.sendMessage(
      `Creating report pages using planId: ${planId!} companyId: ${companyId!}`
    )

    await Axios.get(`/api/plans/${planId!}/reports`, {
      params: {companyId, template: "DEFAULT"},
    })
      .then((response: AxiosResponse) => {
        this.report = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.sendMessage(reason.response?.data.message)
      })

    this.sendMessage(`data loaded`)
  }

  async mounted() {
    this.$nextTick(() => {
      setTimeout(this.waitForReportReady, 2000)
    })
  }

  private waitForReportReady() {
    if (this.report == null) {
      setTimeout(this.waitForReportReady, 1000)
    } else {
      setTimeout(this.sendReportReadyEvent, 6000)
    }
  }

  private sendReportReadyEvent() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window.sendReportReady === "function") {
      this.sendMessage("Event: sendReportReady")
      const reportReadyEvent = new ReportReadyEvent("PolicyCommitteeReport.pdf")
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.sendReportReady("", reportReadyEvent)
    }
  }

  private sendMessage(message: string) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window.sendReportMessage === "function") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.sendReportMessage(message)
    }
  }

  private reportPageBinding(number: number, name: string): PageBinding | null {
    return this.report == null
      ? null
      : {report: this.report, number, name, engagedOnly: this.engagedOnly}
  }

  get components(): Array<{name: string; page: number}> {
    return this.report!.reportPages.length > 0
      ? this.report!.reportPages.filter(
          rp => rp.templatePageName !== PDF_SOURCE_PAGE
        ).map(rp => ({name: rp.templatePageName!, page: rp.templatePageNumber}))
      : components().map((c, i) => ({name: c, page: i + 1}))
  }

  get style(): string | null {
    return this.preview ? "border: 1px solid var(--background-color)" : null
  }
}
